import { styled } from '@linaria/react';
import { useState } from 'react';
import {
  type CapabilitySection,
  CapabilityMode,
  CapabilitySectionKind,
  type StandaloneCapability,
} from '../../../../api/agents-client/agent-client.type';
import iconJira from '../../../../assets/icons/iconJira.svg';
import iconADO from '../../../integrations/assets/iconADO.svg';
import { Icon } from '../../../../ui-library/icon/icon';
import { Switch } from '../../../../ui-library/switch/switch';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { IconTile } from '../shared/settings/icon-tile';
import {
  ChevronToggleButton,
  ExpandButton,
  ExpandChevron,
  SettingsCard,
  SettingsCardHeader,
} from '../shared/settings/settings-card';
import { getSectionVisual } from './agent-customize.constants';
import {
  getApprovalAggregate,
  getApprovalSectionSummary,
  getStandaloneEnableState,
  getStandaloneEnableSummary,
  isStandaloneSectionOff,
} from './agent-customize.helpers';
import type { CustomizeDraft, CustomizeHandlers } from './agent-customize.type';
import { ApprovalGroupSelect } from './approval-group-select';
import { CapabilityBucketCard } from './capability-bucket';
import { CapabilityGroupRow } from './capability-group';

const SECTION_LOGOS: Record<string, { src: string; alt: string }> = {
  jira: { src: iconJira, alt: 'Jira' },
  azure_devops: { src: iconADO, alt: 'Azure DevOps' },
};

interface CapabilitySectionViewProps {
  section: CapabilitySection;
  draft: CustomizeDraft;
  handlers: CustomizeHandlers;
}

/**
 * Renders one top-level capability section, dispatching on its kind: buckets
 * render as a `CapabilityBucketCard`; standalone capabilities render as a
 * `StandaloneCapabilityCard`, which itself branches on the section's mode
 * (enable → on/off toggle, approval → approval control).
 *
 * @param {CapabilitySectionViewProps} props - The section, full draft, and handlers
 * @returns {JSX.Element} The section card
 */
export function CapabilitySectionView({ section, draft, handlers }: CapabilitySectionViewProps) {
  if (section.kind === CapabilitySectionKind.Bucket) {
    return <CapabilityBucketCard bucket={section} enabledTools={draft.enabledTools} handlers={handlers} />;
  }
  return <StandaloneCapabilityCard section={section} draft={draft} handlers={handlers} />;
}

interface StandaloneCapabilityCardProps {
  section: StandaloneCapability;
  draft: CustomizeDraft;
  handlers: CustomizeHandlers;
}

/**
 * A standalone capability section rendered as a card, dispatching on `mode`:
 *
 * - Enable mode (e.g. Office / Word & Excel): the header carries a single
 *   on/off toggle that enables or disables the whole section's tools; expanding
 *   reveals its read and write tool lists.
 * - Approval mode (e.g. Slash commands, Automations, Targets, Jira): the header
 *   carries an approval dropdown — tools are always available; expanding reveals
 *   an explainer plus read (always-on) and write (approval-controlled) groups.
 *
 * @param {StandaloneCapabilityCardProps} props - The section, full draft, and handlers
 * @returns {JSX.Element} The standalone capability card
 */
function StandaloneCapabilityCard({ section, draft, handlers }: StandaloneCapabilityCardProps) {
  const [expanded, setExpanded] = useState(false);
  const visual = getSectionVisual(section.id);
  const logo = SECTION_LOGOS[section.id];
  const badgeBg = logo ? 'var(--sky-lightest)' : visual.badgeBg;

  const badge = (
    <IconTile background={badgeBg} size="lg">
      {logo ? (
        <img src={logo.src} alt={logo.alt} width={24} height={24} />
      ) : (
        <Icon name={visual.icon} size={24} color={visual.color} />
      )}
    </IconTile>
  );

  const chevron = (
    <ChevronToggleButton
      type="button"
      aria-expanded={expanded}
      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${section.name} details`}
      onClick={() => setExpanded((prev) => !prev)}
    >
      <ExpandChevron expanded={expanded} size={18} color="var(--ink-lighter)" />
    </ChevronToggleButton>
  );

  // Enable mode: a single section-wide on/off toggle (e.g. Word & Excel).
  if (section.mode === CapabilityMode.Enable) {
    const enableState = getStandaloneEnableState(section, draft.enabledTools);
    // Dim the tool list only when the section is fully disabled (every optional
    // tool off). A partially-enabled section keeps its still-enabled and
    // always-on tools interactive — see `isStandaloneSectionOff`.
    const sectionOff = isStandaloneSectionOff(section, draft.enabledTools);

    return (
      <Card>
        <SettingsCardHeader>
          <ExpandButton
            type="button"
            aria-expanded={expanded}
            aria-label={`${section.name} tools`}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {badge}
            <Info>
              <Text size="medium" weight="medium" color="var(--ink-darkest)">
                {section.name}
              </Text>
              <Text size="small" color="var(--ink-lighter)">
                {getStandaloneEnableSummary(section, draft.enabledTools)}
              </Text>
            </Info>
          </ExpandButton>
          {enableState.hasOptional && (
            <Tooltip variant="black" label={`Enable or disable ${section.name}`}>
              <SwitchWrap>
                <Switch
                  on={enableState.allOn}
                  size="lg"
                  ariaLabel={`Toggle ${section.name}`}
                  onToggle={() => handlers.setTools(enableState.optionalNames, !enableState.allOn)}
                />
              </SwitchWrap>
            </Tooltip>
          )}
          {chevron}
        </SettingsCardHeader>

        {expanded && (
          <Body>
            {section.read.length > 0 && (
              <CapabilityGroupRow
                kind="read"
                mode={CapabilityMode.Enable}
                tools={section.read}
                enabledTools={draft.enabledTools}
                parentDisabled={sectionOff}
              />
            )}
            {section.write.length > 0 && (
              <CapabilityGroupRow
                kind="write"
                mode={CapabilityMode.Enable}
                tools={section.write}
                enabledTools={draft.enabledTools}
                parentDisabled={sectionOff}
              />
            )}
          </Body>
        )}
      </Card>
    );
  }

  // Approval mode: per-write approval control (e.g. Slash, Automations, Jira).
  const aggregate = getApprovalAggregate(section.write, draft.approvals);
  const writeNames = section.write.map((tool) => tool.name);

  return (
    <Card>
      <SettingsCardHeader>
        <ExpandButton
          type="button"
          aria-expanded={expanded}
          aria-label={`${section.name} details`}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {badge}
          <Info>
            <Text size="medium" weight="medium" color="var(--ink-darkest)">
              {section.name}
            </Text>
            <Text size="small" color="var(--ink-lighter)">
              {getApprovalSectionSummary(section, draft.approvals)}
            </Text>
          </Info>
        </ExpandButton>
        {writeNames.length > 0 && (
          <ApprovalGroupSelect
            value={aggregate}
            dataBf={`approval-select-${section.id}`}
            onChange={(next) => handlers.setApprovals(writeNames, next === 'approval')}
          />
        )}
        {chevron}
      </SettingsCardHeader>

      {expanded && (
        <Body>
          <Explainer size="tiny" color="var(--ink-lighter)">
            {writeNames.length > 0
              ? 'These tools are always available. Turn off approval to let Flora run write & delete actions without asking first.'
              : 'These tools are always available.'}
          </Explainer>
          {section.read.length > 0 && (
            <CapabilityGroupRow
              kind="read"
              mode={section.mode}
              tools={section.read}
              enabledTools={draft.enabledTools}
            />
          )}
          {section.write.length > 0 && (
            <CapabilityGroupRow
              kind="write"
              mode={section.mode}
              tools={section.write}
              enabledTools={draft.enabledTools}
              approvals={draft.approvals}
              onApprovalChange={(name, approvalRequired) => handlers.setApprovals([name], approvalRequired)}
            />
          )}
        </Body>
      )}
    </Card>
  );
}

// Cards sit in the settings section's stack, which spaces them.
const Card = SettingsCard;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

const Body = styled.div`
  padding: 0 20px 14px;
`;

const Explainer = styled(Text)`
  display: block;
  padding: 2px 0 8px;
`;

const SwitchWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;
